import { useEffect, useMemo, useRef } from "react";
import type { WorkflowGuidanceSession } from "../workflowGuidance/types";

type UseCreateSocialClipsAutostartArgs = {
    session: WorkflowGuidanceSession | null;
    projectTracks: any[];
    transcriptWords: any[];
    onOpenCreateClips: () => void;
};

const normalize = (value: unknown): string => String(value ?? "").trim().toLowerCase();

const isTranscriptionCompleted = (value: unknown): boolean =>
    normalize(value) === "completed";

const isTranscriptionTerminalWithoutTranscript = (value: unknown): boolean => {
    const state = normalize(value);
    return state === "failed" || state === "cancelled" || state === "silent" || state === "not_needed";
};

export const useCreateSocialClipsAutostart = ({
    session,
    projectTracks,
    transcriptWords,
    onOpenCreateClips,
}: UseCreateSocialClipsAutostartArgs) => {
    const openedSessionKeysRef = useRef<Set<string>>(new Set());

    const currentTrack = useMemo(() => {
        if (!session || session.workflow !== "create_social_clips") return null;

        const safeTrackId = String(session.trackId || "").trim();
        const safeAssetId = String(session.assetId || "").trim();

        if (safeTrackId) {
            const byTrackId = projectTracks.find((track: any) => {
                const trackId = String(track?.trackId || track?.id || "").trim();
                return trackId === safeTrackId;
            });
            if (byTrackId) return byTrackId;
        }

        if (!safeAssetId) return null;
        return projectTracks.find((track: any) => String(track?.assetId || "").trim() === safeAssetId) ?? null;
    }, [projectTracks, session]);

    useEffect(() => {
        if (!session || session.workflow !== "create_social_clips") return;

        const sessionKey = String(session.key || "").trim();
        if (!sessionKey || openedSessionKeysRef.current.has(sessionKey)) return;
        if (!currentTrack) return;

        if (isTranscriptionTerminalWithoutTranscript(currentTrack?.transcriptionState)) {
            openedSessionKeysRef.current.add(sessionKey);
            return;
        }

        if (!isTranscriptionCompleted(currentTrack?.transcriptionState)) {
            return;
        }

        const resolvedTrackId = String(currentTrack?.trackId || currentTrack?.id || session.trackId || "").trim();
        if (!resolvedTrackId) return;

        const hasTranscriptWords = transcriptWords.some(
            (word: any) => String(word?.sourceAssetId || "").trim() === resolvedTrackId,
        );
        if (!hasTranscriptWords) return;

        openedSessionKeysRef.current.add(sessionKey);
        onOpenCreateClips();
    }, [currentTrack, onOpenCreateClips, session, transcriptWords]);
};

export default useCreateSocialClipsAutostart;
